import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone } from "lucide-react";

// -----------------------------------------------------------------------------
// PolicyLayout — shared shell used by /about, /privacy, /terms, /legal
// Keeps typography, header and footer consistent with the Fender homepage so
// the four documents feel like part of the same product experience.
// Content is passed as a structured array (see block renderers below) instead
// of one long JSX blob — makes each policy page short, editable and testable.
// -----------------------------------------------------------------------------

export const LEGAL_NAME = "Iradium Automobiles Private Limited";
export const COPYRIGHT_YEAR = 2026;

// Content block renderers ------------------------------------------------------
export const H2 = ({ children, id }) => (
  <h2 id={id} className="text-[22px] md:text-[26px] font-bold text-[#1d3557] tracking-tight mt-12 mb-4 scroll-mt-24">
    {children}
  </h2>
);
export const H3 = ({ children }) => (
  <h3 className="text-[16px] md:text-[17px] font-bold text-[#1d3557] mt-6 mb-2">{children}</h3>
);
export const P = ({ children }) => (
  <p className="text-[14px] md:text-[15px] text-[#1d3557] leading-relaxed my-3">{children}</p>
);
export const UL = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-[14px] md:text-[15px] text-[#1d3557] leading-relaxed my-3 marker:text-[#457b9d]">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);
export const Callout = ({ children }) => (
  <div className="bg-[#f1faee] border-l-4 border-[#457b9d] rounded-r-md px-5 py-4 my-6 text-[14px] text-[#1d3557] leading-relaxed">
    {children}
  </div>
);

// Renders a data-driven block schema
export const Blocks = ({ blocks }) => (
  <>
    {blocks.map((b, i) => {
      if (b.type === "h2") return <H2 key={i} id={b.id}>{b.text}</H2>;
      if (b.type === "h3") return <H3 key={i}>{b.text}</H3>;
      if (b.type === "p")  return <P key={i}>{b.text}</P>;
      if (b.type === "ul") return <UL key={i} items={b.items} />;
      if (b.type === "callout") return <Callout key={i}>{b.text}</Callout>;
      return null;
    })}
  </>
);

// Compact header bar (does not reuse the marketing Header — kept minimal so
// the reader focuses on the policy text)
export const PolicyHeader = () => (
  <header className="w-full bg-white border-b border-[#e6edf1] sticky top-0 z-30" data-testid="policy-header">
    <div className="max-w-[900px] mx-auto px-6 h-14 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2" data-testid="policy-back-home">
        <img
          src="/fender-logo-lockup.png"
          alt="Fender by SparesHub"
          className="h-8 w-auto"
          width="720"
          height="208"
        />
      </Link>
      <Link to="/" className="text-[13px] text-[#457b9d] hover:text-[#e63946] font-semibold inline-flex items-center gap-1.5" data-testid="policy-home-link">
        <ArrowLeft size={13} /> Back to home
      </Link>
    </div>
  </header>
);

export const PolicyFooter = () => (
  <footer className="border-t border-[#e6edf1] bg-white" data-testid="policy-footer">
    <div className="max-w-[900px] mx-auto px-6 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-[12px] text-[#457b9d]">
      <div>© {COPYRIGHT_YEAR} <span className="notranslate">{LEGAL_NAME}</span>. All rights reserved.</div>
      <div className="font-semibold text-[#1d3557]">Fender by SparesHub</div>
    </div>
  </footer>
);

// Reusable contact card used by every policy page footer body
export const ContactCard = ({ personName = "Tapas Gupta" }) => (
  <div className="bg-[#f1faee] rounded-xl p-6 my-8 border border-[#a8dadc]/40" data-testid="policy-contact-card">
    <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#457b9d]">Contact</div>
    <div className="mt-2 text-[15px] font-bold text-[#1d3557]">{personName}</div>
    <div className="text-[13px] text-[#1d3557] mt-1">
      <span className="notranslate">Iradium Automobiles Private Limited</span>
      <br />Pune, Maharashtra, India
    </div>
    <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-[13px]">
      <a href="mailto:contact@fenderapp.com" className="inline-flex items-center gap-1.5 text-[#457b9d] hover:text-[#e63946] font-semibold">
        <Mail size={13} /> contact@fenderapp.com
      </a>
      <a href="tel:+919422799760" className="inline-flex items-center gap-1.5 text-[#457b9d] hover:text-[#e63946] font-semibold">
        <Phone size={13} /> +91-9422799760
      </a>
    </div>
  </div>
);

// Full page shell
export default function PolicyLayout({ title, effective, subtitle, children, testId }) {
  return (
    <div
      className="min-h-screen bg-white text-[#1d3557]"
      style={{ fontFamily: '"Noto Sans","Noto Sans Devanagari","Noto Sans Gujarati","Noto Sans Kannada","Noto Sans Tamil","Noto Sans Telugu","Noto Sans Malayalam",Arial,sans-serif' }}
      data-testid={testId}
    >
      <PolicyHeader />
      <main className="max-w-[900px] mx-auto px-6 py-10 md:py-14">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#e63946] mb-3">Fender by SparesHub</div>
        <h1 className="text-[32px] md:text-[40px] font-bold text-[#1d3557] leading-[1.1] tracking-tight">{title}</h1>
        {subtitle && <p className="mt-3 text-[15px] text-[#457b9d] max-w-2xl">{subtitle}</p>}
        {effective && (
          <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full bg-[#f1faee] text-[12px] font-semibold text-[#457b9d] border border-[#a8dadc]/40">
            Effective Date: {effective}
          </div>
        )}
        <div className="mt-8 prose prose-slate max-w-none">
          {children}
        </div>
      </main>
      <PolicyFooter />
    </div>
  );
}
